import { useState } from 'react';
import type { CSSProperties } from 'react';
import { CANVAS, type MailPieceSize } from '../../createEditor/mailPieceTemplates';

export interface NewProjectConfig {
  name: string;
  width: number;
  height: number;
  duration: number;
  frameRate: number;
}

interface NewProjectModalProps {
  onCreated: (cfg: NewProjectConfig) => void;
  /**
   * What the composition is being made for. Direct mail is offered postcard canvases at
   * print resolution instead of the screen sizes, because a card designed on a 1920x1080
   * canvas has the wrong proportions and is upscaled on its way to the printer.
   */
  purpose?: 'screen' | 'direct_mail';
}

/** 300 dots per inch: what the printer wants, and the whole reason mail needs its own list. */
const DPI = 300;

/**
 * Postcard canvases, at bleed.
 *
 * The named size is what arrives; the canvas is bigger because Lob trims {@link TRIM} off
 * every edge. Designing at the finished size is the classic way to lose a quarter inch of
 * artwork on all four sides, so the preset is the size we actually send.
 */
const MAIL_PRESETS = (Object.keys(CANVAS) as MailPieceSize[]).map((size) => ({
  label: `${size} postcard`,
  hint: `${CANVAS[size].width} x ${CANVAS[size].height} in at ${DPI}dpi, with bleed`,
  w: Math.round(CANVAS[size].width * DPI),
  h: Math.round(CANVAS[size].height * DPI),
}));

const SCREEN_PRESETS = [
  { label: '1920 x 1080 (HD)', hint: '', w: 1920, h: 1080 },
  { label: '1080 x 1920 (Vertical)', hint: '', w: 1080, h: 1920 },
  { label: '1080 x 1080 (Square)', hint: '', w: 1080, h: 1080 },
  { label: '800 x 600', hint: '', w: 800, h: 600 },
  { label: '1280 x 720 (720p)', hint: '', w: 1280, h: 720 },
];

export function NewProjectModal({ onCreated, purpose = 'screen' }: NewProjectModalProps) {
  const forMail = purpose === 'direct_mail';
  const presets = forMail ? MAIL_PRESETS : SCREEN_PRESETS;
  const first = presets[0];

  const [name, setName] = useState(forMail ? 'Untitled Postcard' : 'Untitled Project');
  const [width, setWidth] = useState(first.w);
  const [height, setHeight] = useState(first.h);
  // A postcard does not move. Kept in state so the payload stays one shape, but never shown
  // for mail: asking a designer for a frame rate on a printed card is how the editor earned
  // its reputation for being for video people.
  const [duration, setDuration] = useState(forMail ? 1 : 10);
  const [frameRate, setFrameRate] = useState(24);

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'rgba(15,23,42,0.45)',
        zIndex: 10000,
      }}
    >
      <div
        style={{
          background: '#ffffff',
          border: '1px solid #e2e8f0',
          borderRadius: 12,
          padding: 32,
          width: 400,
          color: '#1e293b',
          boxShadow: '0 8px 30px rgba(0,0,0,0.12)',
        }}
      >
        <h2 style={{ margin: '0 0 24px', fontSize: 20, fontWeight: 600 }}>
          {forMail ? 'New Postcard' : 'New Project'}
        </h2>

        <div style={{ marginBottom: 16 }}>
          <label style={{ fontSize: 12, color: '#64748b', display: 'block', marginBottom: 4, fontWeight: 500 }}>
            Project Name
          </label>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            style={{ ...modalInputStyle, width: '100%' }}
            autoFocus
          />
        </div>

        <div style={{ marginBottom: 16 }}>
          <label style={{ fontSize: 12, color: '#64748b', display: 'block', marginBottom: 4, fontWeight: 500 }}>
            {forMail ? 'Card size' : 'Size Presets'}
          </label>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {presets.map((p) => {
              const on = width === p.w && height === p.h;
              return (
                <button
                  key={p.label}
                  title={p.hint || undefined}
                  onClick={() => {
                    setWidth(p.w);
                    setHeight(p.h);
                  }}
                  style={{
                    ...modalBtnStyle,
                    textAlign: 'left',
                    padding: forMail ? '6px 10px' : '4px 8px',
                    background: on ? '#2563eb' : '#f1f5f9',
                    color: on ? '#ffffff' : '#475569',
                    borderColor: on ? '#2563eb' : '#e2e8f0',
                  }}
                >
                  {p.label}
                  {p.hint && (
                    <span style={{ display: 'block', fontSize: 10, opacity: 0.75 }}>
                      {p.hint}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
          <div style={{ flex: 1 }}>
            <label style={{ fontSize: 12, color: '#64748b', display: 'block', marginBottom: 4, fontWeight: 500 }}>
              Width
            </label>
            <input
              type="number"
              value={width}
              onChange={(e) => setWidth(parseInt(e.target.value) || 0)}
              style={{ ...modalInputStyle, width: '100%' }}
            />
          </div>
          <div style={{ flex: 1 }}>
            <label style={{ fontSize: 12, color: '#64748b', display: 'block', marginBottom: 4, fontWeight: 500 }}>
              Height
            </label>
            <input
              type="number"
              value={height}
              onChange={(e) => setHeight(parseInt(e.target.value) || 0)}
              style={{ ...modalInputStyle, width: '100%' }}
            />
          </div>
        </div>

        <div style={{ display: forMail ? 'none' : 'flex', gap: 12, marginBottom: 24 }}>
          <div style={{ flex: 1 }}>
            <label style={{ fontSize: 12, color: '#64748b', display: 'block', marginBottom: 4, fontWeight: 500 }}>
              Duration (sec)
            </label>
            <input
              type="number"
              min={1}
              value={duration}
              onChange={(e) => setDuration(parseFloat(e.target.value) || 0)}
              style={{ ...modalInputStyle, width: '100%' }}
            />
          </div>
          <div style={{ flex: 1 }}>
            <label style={{ fontSize: 12, color: '#64748b', display: 'block', marginBottom: 4, fontWeight: 500 }}>
              Frame Rate
            </label>
            <select
              value={frameRate}
              onChange={(e) => setFrameRate(parseInt(e.target.value))}
              style={{ ...modalInputStyle, width: '100%' }}
            >
              {[24, 30, 60].map((fps) => (
                <option key={fps} value={fps}>
                  {fps} fps
                </option>
              ))}
            </select>
          </div>
        </div>

        <button
          onClick={() => onCreated({ name, width, height, duration, frameRate })}
          disabled={!name || width < 1 || height < 1 || duration < 1}
          style={{
            width: '100%',
            padding: '10px 0',
            background: '#2563eb',
            border: 'none',
            borderRadius: 6,
            color: '#fff',
            fontSize: 14,
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          {forMail ? 'Create Postcard' : 'Create Project'}
        </button>
      </div>
    </div>
  );
}

const modalInputStyle: CSSProperties = {
  background: '#f8fafc',
  border: '1px solid #e2e8f0',
  borderRadius: 6,
  color: '#1e293b',
  padding: '8px 10px',
  fontSize: 14,
  outline: 'none',
};

const modalBtnStyle: CSSProperties = {
  border: '1px solid #e2e8f0',
  borderRadius: 4,
  color: '#475569',
  padding: '4px 8px',
  fontSize: 11,
  cursor: 'pointer',
};
